// Iter70 — Universal two-way sync between filter state and URL query params.
//
// Design goals:
//   1. Debounce URL writes (default 300ms) so rapid typing in a search box
//      doesn't spam history entries.
//   2. Hydrate initial state from the URL exactly once on mount (any missing
//      key falls back to the caller's default).
//   3. Omit keys from the URL when their value equals the caller-provided
//      default — keeps the address bar clean and bookmarkable.
//   4. Support both simple key mappings (state key ↔ url param string) and
//      per-key codecs when the domain vocabulary differs from the URL
//      vocabulary (e.g. `approved: yes|no|all` ↔ `stage=approved|pending`).
//
// Usage:
//   const DEFAULTS = { search: "", status: "all", from: "2026-01-01" };
//   const [filters, setFilters] = useState(DEFAULTS);
//   useFilterUrlSync({
//     filters,
//     defaults: DEFAULTS,
//     paramMap: {
//       search: "search",
//       status: "status",
//       from: "from",
//       // Custom codec form:
//       approved: {
//         param: "stage",
//         toUrl: (v) => (v === "yes" ? "approved" : v === "no" ? "pending" : ""),
//         fromUrl: (v) => (v === "approved" ? "yes" : v === "pending" ? "no" : "all"),
//       },
//     },
//     hydrate: (patch) => setFilters((f) => ({ ...f, ...patch })),
//   });
//
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

function resolveEntry(entry) {
    if (typeof entry === "string") {
        return { param: entry, toUrl: null, fromUrl: null };
    }
    return {
        param: entry.param,
        toUrl: entry.toUrl || null,
        fromUrl: entry.fromUrl || null,
    };
}

function defaultToUrl(val) {
    if (typeof val === "boolean") return val ? "true" : "";
    if (val == null) return "";
    return String(val);
}

function defaultFromUrl(raw, def) {
    if (typeof def === "boolean") return raw === "true";
    return raw;
}

// Serialise the filters object into a stable string key so React's effect
// deps don't trigger on unrelated re-renders. Only the values covered by
// `paramMap` are included; anything else on the filters object is ignored.
function serialiseFilters(filters, paramMap) {
    const parts = [];
    for (const stateKey of Object.keys(paramMap)) {
        const v = filters[stateKey];
        if (typeof v === "boolean") parts.push(`${stateKey}=${v ? "1" : "0"}`);
        else if (v == null) parts.push(`${stateKey}=`);
        else parts.push(`${stateKey}=${String(v)}`);
    }
    return parts.join("&");
}

export default function useFilterUrlSync({
    filters,
    defaults,
    paramMap,
    hydrate,
    debounceMs = 300,
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const hydratedRef = useRef(false);
    const timerRef = useRef(null);
    // Keep the latest filter values reachable from the debounced callback
    // without depending on the (new-object-every-render) `filters` prop.
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    // Hydrate once on mount from URL → state.
    useEffect(() => {
        if (hydratedRef.current) return;
        hydratedRef.current = true;
        const patch = {};
        for (const [stateKey, entry] of Object.entries(paramMap)) {
            const { param, fromUrl } = resolveEntry(entry);
            if (searchParams.has(param)) {
                const raw = searchParams.get(param);
                const def = defaults[stateKey];
                patch[stateKey] = fromUrl ? fromUrl(raw, def) : defaultFromUrl(raw, def);
            }
        }
        if (Object.keys(patch).length > 0) hydrate(patch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reverse sync: state → URL, debounced. The dep is a stable string
    // built from the primitive filter values — passing an object literal
    // as the dep would cause React to fire the effect (and reset the
    // debounce timer) on every unrelated re-render, so the timer would
    // never actually elapse.
    const depKey = serialiseFilters(filters, paramMap);
    useEffect(() => {
        if (!hydratedRef.current) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            const currentFilters = filtersRef.current;
            const next = new URLSearchParams(searchParams);
            for (const [stateKey, entry] of Object.entries(paramMap)) {
                const { param, toUrl } = resolveEntry(entry);
                const val = currentFilters[stateKey];
                const def = defaults[stateKey];
                const eq = (a, b) => a === b
                    || (a == null && b == null)
                    || (typeof def === "boolean" && !!a === !!b)
                    || (typeof def === "string" && String(a ?? "") === String(b ?? ""));
                if (eq(val, def)) {
                    next.delete(param);
                    continue;
                }
                const out = toUrl ? toUrl(val) : defaultToUrl(val);
                if (out === "" || out == null) next.delete(param);
                else next.set(param, out);
            }
            const cur = searchParams.toString();
            const nxt = next.toString();
            if (cur !== nxt) setSearchParams(next, { replace: true });
        }, debounceMs);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depKey]);
}
